"use client";

import { useEffect, useId, useRef, useState, type CSSProperties } from "react";
import { Link } from "@/i18n/navigation";
import { playerDetailHref } from "@/components/player-detail/href";
import styles from "./PostCreatorRow.module.css";

export const POST_ROSTER_CATEGORIES = [
  "progression",
  "pve",
  "pvp",
  "twitch",
  "warOrders",
] as const;
export type PostRosterCategory = (typeof POST_ROSTER_CATEGORIES)[number];

export type PostCreatorRowProps = {
  animationIndex?: number;
  creator: {
    name: string;
    twitchLogin: string;
    faction: string;
    rank: string;
    role: string;
    points: string;
    scores: Record<PostRosterCategory, string>;
  };
  categoryLabels: Record<PostRosterCategory, string>;
  ptsLabel: string;
  viewBreakdownLabel: string;
  viewLessLabel: string;
};

/** Post-owned copy of the CreatorRow layout, intentionally without legion/emblem. */
export function PostCreatorRow({
  animationIndex = 0,
  creator,
  categoryLabels,
  ptsLabel,
  viewBreakdownLabel,
  viewLessLabel,
}: PostCreatorRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [visible, setVisible] = useState(false);
  const rowRef = useRef<HTMLLIElement>(null);
  const listId = useId();

  useEffect(() => {
    const row = rowRef.current;
    if (!row || !window.IntersectionObserver) return;
    const motion = window.matchMedia(
      "(min-width: 1001px) and (prefers-reduced-motion: no-preference)",
    );
    let observer: IntersectionObserver | undefined;
    const configure = () => {
      observer?.disconnect();
      setVisible(false);
      if (!motion.matches) return;
      const siblings = row.parentElement?.children;
      const trigger = siblings?.[Math.min(2, siblings.length - 1)] ?? row;
      let rosterReady = false;
      let rowReady = false;
      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const clearlyVisible =
              entry.isIntersecting && entry.intersectionRatio >= 0.65;
            // Latch the shared gate once the third name is clearly on screen.
            if (entry.target === trigger && clearlyVisible) rosterReady = true;
            if (entry.target === row) rowReady = clearlyVisible;
          }
          if (rosterReady && rowReady) {
            setVisible(true);
            observer?.disconnect();
          }
        },
        {
          threshold: 0.65,
          rootMargin: "0px 0px -10% 0px",
        },
      );
      observer.observe(trigger);
      observer.observe(row);
    };
    configure();
    motion.addEventListener("change", configure);
    return () => {
      observer?.disconnect();
      motion.removeEventListener("change", configure);
    };
  }, []);

  return (
    <li
      ref={rowRef}
      data-visible={visible}
      style={
        {
          "--roster-offset": `${Math.min(animationIndex, 9) * 16}px`,
          "--roster-delay": `${Math.min(animationIndex, 9) * 25}ms`,
        } as CSSProperties
      }
      className={`${styles.row} ${creator.faction === "elyos" ? styles.elyos : creator.faction === "asmodian" ? styles.asmodian : ""}`}
    >
      <div className={styles.inner}>
        <div className={styles.left}>
          <span className={styles.rank}>{creator.rank}</span>
          <div className={styles.identity}>
            {/* <span className={styles.role}>{creator.role}</span> */}
            <Link
              href={playerDetailHref(creator.twitchLogin)}
              prefetch={false}
              className={styles.nameLink}
            >
              {creator.name}
            </Link>
          </div>
        </div>
        <dl className={styles.stats}>
          {POST_ROSTER_CATEGORIES.map((key) => (
            <div key={key} className={styles.stat}>
              <dt className={styles.statLabel}>{categoryLabels[key]}</dt>
              <dd className={styles.statValue}>{creator.scores[key]}</dd>
            </div>
          ))}
        </dl>
        <div className={styles.pts}>
          <span className={styles.ptsLabel}>{ptsLabel}</span>
          <span className={styles.ptsValue}>{creator.points}</span>
        </div>
        <div className={styles.bar}>
          <button
            type="button"
            className={styles.breakdownBtn}
            aria-expanded={expanded}
            aria-controls={listId}
            onClick={() => setExpanded((open) => !open)}
          >
            {expanded ? viewLessLabel : viewBreakdownLabel}
          </button>
        </div>
        <ul id={listId} className={styles.breakdownList} hidden={!expanded}>
          {POST_ROSTER_CATEGORIES.map((key) => (
            <li key={key} className={styles.breakdownItem}>
              <span>{categoryLabels[key]}</span>
              <span>
                {creator.scores[key]} <small>{ptsLabel}</small>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </li>
  );
}
